import React, { useState } from 'react';
import { AlertTriangle, Send, Loader, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/apiService';
import MediaCapture from '../common/MediaCapture';
import AudioRecorder from '../common/AudioRecorder';
import IssueTypeSelector from '../common/IssueTypeSelector';

const ReportForm = ({
  bedData,
  farmData,
  plotData,
  plantData,
  onSuccess,
  onCancel,
  className = ''
}) => {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);
  const [isSharingTikTok, setIsSharingTikTok] = useState(false);

  // Determine report type and data source
  const isPlantReport = !!plantData;
  const isBedReport = !!bedData && !plantData;
  const isFarmReport = !!farmData && !bedData && !plotData && !plantData;
  const isPlotReport = !!plotData && !bedData && !plantData;

  // Form state
  const [formData, setFormData] = useState({
    description: '',
    notes: '',
    selectedIssues: [],
    customIssues: [],
    capturedFiles: [],
    audioFile: null,
    postToFacebook: false,
    postToInstagram: false,
    socialDescription: ''
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    const hasContent = formData.description.trim() ||
                      formData.notes.trim() ||
                      formData.selectedIssues.length > 0 ||
                      formData.customIssues.length > 0 ||
                      formData.capturedFiles.length > 0 ||
                      formData.audioFile;

    if (!hasContent) {
      newErrors.general = 'Please provide at least some information about the issue';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleIssueSelection = ({ selectedIssues, customIssues }) => {
    setFormData(prev => ({ ...prev, selectedIssues, customIssues }));
  };

  const uploadMediaFiles = async (submissionId) => {
    const uploadPromises = [];

    formData.capturedFiles.forEach(file => {
      const fileType = file.type.startsWith('image/') ? 'image' : 'video';
      uploadPromises.push(apiService.uploadMedia(file, fileType, submissionId));
    });

    if (formData.audioFile) {
      uploadPromises.push(apiService.uploadMedia(formData.audioFile, 'audio', submissionId));
    }

    try {
      const results = await Promise.all(uploadPromises);
      console.log('Media upload results:', results);
      return results;
    } catch (error) {
      console.error('Media upload error:', error);
      throw new Error('Failed to upload media files');
    }
  };

  const buildSubmissionData = () => {
    const base = {
      description: formData.description.trim() || '',
      notes: formData.notes.trim() || '',
      issue_types: formData.selectedIssues,
      custom_issue_tags: formData.customIssues,
      observer_name: user?.name || user?.email || 'Anonymous',
      date: new Date().toISOString(),
      images: [],
      videos: [],
      audio: [],
      social_post: formData.postToFacebook || formData.postToInstagram,
      post_to_facebook: formData.postToFacebook,
      post_to_instagram: formData.postToInstagram,
      post_to_tiktok: false,
      social_description: formData.socialDescription.trim()
    };

    if (isPlantReport) {
      const { plant, bed, farm, plot } = plantData;
      return { ...base, plant_id: plant.id, bed_id: bed.id, farm_id: farm.id, plot_id: plot.id, plot_name: plot.name };
    }
    if (isBedReport) {
      const { bed, farm, plot } = bedData;
      return { ...base, bed_id: bed.id, farm_id: farm.id, plot_id: plot.id, plot_name: plot.name };
    }
    if (isPlotReport) {
      const { farm, plot } = plotData;
      return { ...base, farm_id: farm.id, plot_id: plot.id, plot_name: plot.name };
    }
    if (isFarmReport) {
      const { farm } = farmData;
      return { ...base, farm_id: farm.id };
    }
    throw new Error('Invalid report data: plantData, bedData, plotData, or farmData must be provided');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const submissionData = buildSubmissionData();
      console.log('Creating submission with data:', submissionData);

      const submissionResponse = await apiService.createSubmission(submissionData);

      if (!submissionResponse.success) {
        throw new Error(submissionResponse.message || 'Failed to create submission');
      }

      const submissionId = submissionResponse.data.id;
      console.log('Submission created with ID:', submissionId);

      const hasMedia = formData.capturedFiles.length > 0 || formData.audioFile;
      if (hasMedia) {
        try {
          await uploadMediaFiles(submissionId);
        } catch (uploadError) {
          console.warn('Media upload failed, but submission was created:', uploadError);
        }
      }

      // Trigger Facebook/Instagram posting if selected
      if (formData.postToFacebook || formData.postToInstagram) {
        try {
          await apiService.triggerSocialPost(submissionId);
          console.log('Social media post triggered');
        } catch (socialError) {
          console.warn('Failed to trigger social post:', socialError);
        }
      }

      // Reset form
      setFormData({
        description: '',
        notes: '',
        selectedIssues: [],
        customIssues: [],
        capturedFiles: [],
        audioFile: null,
        postToFacebook: false,
        postToInstagram: false,
        socialDescription: ''
      });

      setSubmittedId(submissionId);
      setShowSuccessDialog(true);

    } catch (error) {
      console.error('Report submission error:', error);
      toast.error(error.message || 'Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const generatePKCE = async () => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    const verifier = btoa(String.fromCharCode(...array))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    const data = new TextEncoder().encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    const challenge = btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    return { verifier, challenge };
  };

  const handleShareToTikTok = async () => {
    if (!submittedId) return;
    setIsSharingTikTok(true);
    try {
      const { verifier, challenge } = await generatePKCE();
      localStorage.setItem('tiktok_pkce_verifier', verifier);
      const response = await apiService.getTikTokUserShareAuthURL(submittedId, challenge);
      const authURL = response.data?.auth_url;
      if (!authURL) {
        toast.error('Failed to get TikTok share URL');
        setIsSharingTikTok(false);
        return;
      }

      const width = 600, height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(
        authURL,
        'tiktok-user-share',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
      );

      const handleMessage = (event) => {
        if (event.data?.type !== 'tiktok-user-share-done') return;
        window.removeEventListener('message', handleMessage);
        clearInterval(pollTimer);
        setIsSharingTikTok(false);
        if (event.data.success) {
          toast.success('Shared to TikTok!');
          handleDone();
        } else {
          toast.error(event.data.error || 'Failed to share to TikTok');
        }
      };
      window.addEventListener('message', handleMessage);

      const pollTimer = setInterval(() => {
        if (popup?.closed) {
          clearInterval(pollTimer);
          window.removeEventListener('message', handleMessage);
          setIsSharingTikTok(false);
        }
      }, 500);
    } catch (error) {
      console.error('Error sharing to TikTok:', error);
      toast.error('Failed to start TikTok share');
      setIsSharingTikTok(false);
    }
  };

  const handleDone = () => {
    setShowSuccessDialog(false);
    if (onSuccess) onSuccess({ id: submittedId });
  };

  // Success dialog overlay
  if (showSuccessDialog) {
    return (
      <div className={className}>
        <div className="flex flex-col items-center justify-center py-12 px-6 text-center space-y-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle className="w-9 h-9 text-green-500" />
          </div>

          <div>
            <h3 className="text-xl font-semibold text-gray-900">Report Submitted!</h3>
            <p className="text-sm text-gray-500 mt-1">Your report has been submitted successfully.</p>
          </div>

          <div className="w-full max-w-xs space-y-3">
            <p className="text-sm text-gray-600 font-medium">Would you like to share this report to TikTok?</p>

            <button
              onClick={handleShareToTikTok}
              disabled={isSharingTikTok}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSharingTikTok ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <img src="/tiktok.png" alt="TikTok" className="w-4 h-4" />
              )}
              {isSharingTikTok ? 'Opening TikTok...' : 'Share to TikTok'}
            </button>

            <button
              onClick={handleDone}
              className="w-full px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Header */}
        <div className="border-b border-gray-200 pb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <AlertTriangle className="h-5 w-5 text-orange-500 mr-2" />
            {isPlantReport && `Report Issue for ${plantData.plant.plant_common_name}`}
            {isBedReport && `Report Issue for ${bedData.bed.crop} Bed`}
            {isPlotReport && `Report Issue for ${plotData.plot.name}`}
            {isFarmReport && `Report Issue for ${farmData.farm.name}`}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {isPlantReport && 'Report any issues with this plant. All fields are optional, but please provide as much detail as possible.'}
            {isBedReport && 'Report any issues with this bed. All fields are optional, but please provide as much detail as possible.'}
            {isPlotReport && 'Report any issues with this plot. All fields are optional, but please provide as much detail as possible.'}
            {isFarmReport && 'Report any issues with this farm. All fields are optional, but please provide as much detail as possible.'}
          </p>
        </div>

        {/* General Error */}
        {errors.general && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-600">{errors.general}</p>
          </div>
        )}

        {/* Issue Types */}
        <IssueTypeSelector
          selectedIssues={formData.selectedIssues}
          customIssues={formData.customIssues}
          onSelectionChange={handleIssueSelection}
        />

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
            Issue Description
          </label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Describe the issue you've observed..."
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>

        {/* Notes */}
        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
            Additional Notes
          </label>
          <textarea
            id="notes"
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            placeholder="Any additional observations, suggestions, or context..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>

        {/* Social Media Caption */}
        <div>
          <label htmlFor="socialDescription" className="block text-sm font-medium text-gray-700 mb-2">
            Social Media Caption
          </label>
          <textarea
            id="socialDescription"
            value={formData.socialDescription}
            onChange={(e) => handleInputChange('socialDescription', e.target.value)}
            placeholder="Write a caption for your social media post (optional)..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-400 mt-1">Used as the post caption when sharing to social media. Leave empty to post media only.</p>
        </div>

        {/* Media Capture */}
        <MediaCapture
          onFilesChange={(files) => handleInputChange('capturedFiles', files)}
          maxFileSize={50 * 1024 * 1024}
          maxVideoLength={30}
          label="Upload or Capture Photos & Videos"
        />

        {/* Audio Recorder */}
        <AudioRecorder
          onAudioChange={(audio) => handleInputChange('audioFile', audio)}
          maxDuration={60}
        />

        {/* Report Information */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Reporting for:</h4>
          <div className="text-sm text-gray-600 space-y-1">
            {isPlantReport && (
              <>
                <p><span className="font-medium">Plant:</span> {plantData.plant.plant_common_name} (ID: {plantData.plant.id})</p>
                {plantData.plant.scientific_name && <p><span className="font-medium">Scientific Name:</span> {plantData.plant.scientific_name}</p>}
                <p><span className="font-medium">Bed:</span> {plantData.bed.crop} Bed #{plantData.bed.bed_number}</p>
                <p><span className="font-medium">Plot:</span> {plantData.plot.name}</p>
                <p><span className="font-medium">Farm:</span> {plantData.farm.name}</p>
              </>
            )}
            {isBedReport && (
              <>
                <p><span className="font-medium">Bed:</span> {bedData.bed.crop} Bed (ID: {bedData.bed.id})</p>
                <p><span className="font-medium">Plot:</span> {bedData.plot.name}</p>
                <p><span className="font-medium">Farm:</span> {bedData.farm.name}</p>
              </>
            )}
            {isPlotReport && (
              <>
                <p><span className="font-medium">Plot:</span> {plotData.plot.name} (ID: {plotData.plot.id})</p>
                <p><span className="font-medium">Farm:</span> {plotData.farm.name}</p>
                {plotData.plot.description && <p><span className="font-medium">Description:</span> {plotData.plot.description}</p>}
                {plotData.plot.area && <p><span className="font-medium">Area:</span> {plotData.plot.area} sq ft</p>}
              </>
            )}
            {isFarmReport && (
              <>
                <p><span className="font-medium">Farm:</span> {farmData.farm.name} (ID: {farmData.farm.id})</p>
                {farmData.farm.owner && <p><span className="font-medium">Owner:</span> {farmData.farm.owner}</p>}
                {farmData.farm.address && <p><span className="font-medium">Location:</span> {farmData.farm.address}</p>}
              </>
            )}
            <p><span className="font-medium">Reporter:</span> {user?.name || user?.email || 'Anonymous'}</p>
          </div>
        </div>

        {/* Social Media Posting - Facebook & Instagram only */}
        <div className="border border-gray-200 rounded-lg p-4 space-y-3">
          <h4 className="text-sm font-medium text-gray-700">Share to Social Media</h4>
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.postToFacebook}
                onChange={(e) => handleInputChange('postToFacebook', e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <img src="/facebook.png" alt="Facebook" className="w-5 h-5" />
              <span className="text-sm text-gray-900">Facebook</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.postToInstagram}
                onChange={(e) => handleInputChange('postToInstagram', e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-pink-600 focus:ring-pink-500"
              />
              <img src="/instagram.png" alt="Instagram" className="w-5 h-5" />
              <span className="text-sm text-gray-900">Instagram</span>
            </label>
          </div>
          <p className="text-xs text-gray-400">To share on TikTok, you'll be prompted after submitting the report.</p>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isSubmitting ? (
              <>
                <Loader className="h-4 w-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Submit Report
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReportForm;
